// src/screens/PlayCreator.tsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import type { ReactNode } from "react";
import {
  Stage,
  Layer,
  Image as KonvaImage,
  Circle,
  Arrow as KonvaArrow,
  Group,
  Text,
  Line,
  Rect,
  Shape,
} from "react-konva";
import useImage from "use-image";
import { supabase } from "../supabaseClient";
import { v4 as uuidv4 } from "uuid";
import type { KonvaEventObject } from "konva/lib/Node";
import { useNavigate } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import {
  Trash2,
  ChevronLeft,
  ChevronRight,
  Plus,
  Undo2,
  Lock,
  Unlock,
  RotateCw,
  RotateCcw,
} from "lucide-react";
import i18n from "../i18n";
import PlayArrow from "../components/PlayArrow";

/* petit composant clavier pour la légende des raccourcis */
const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 font-mono text-[10px] leading-none">
    {children}
  </kbd>
);

/* ================= i18n mini-hook (safe) ================= */
function useI18nSafe() {
  const [, setTick] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setTick(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, opts?: Record<string, unknown>): string =>
    opts ? String((i18n.t as any)(key, opts)) : String((i18n.t as any)(key));
  return { t };
}

type Tag = { id: string; name: string };
type Pt = { x: number; y: number };
type Defender = { x: number; y: number; angle: number };
type Phase = {
  players: { x: number; y: number; hasBall?: boolean; number: number }[];
  arrows: {
    from?: { x: number; y: number };
    to?: { x: number; y: number };
    fromPlayerIndex?: number;
    toPlayerIndex?: number;
    type: string;
    control?: { x: number; y: number };
  }[];
  defenders: Defender[];
};

type Option = { value: string; label: string };

/* ================= sélection multiple ================= */
type MultiSel = { players: number[]; defenders: number[]; arrows: number[] };

const selCount = (s: MultiSel) => s.players.length + s.defenders.length + s.arrows.length;

const arrowEnds = (a: Phase["arrows"][number], players: Phase["players"]) => ({
  from: a.fromPlayerIndex !== undefined ? players[a.fromPlayerIndex] : a.from,
  to: a.toPlayerIndex !== undefined ? players[a.toPlayerIndex] : a.to,
});

/** Décale de (dx, dy) les éléments sélectionnés, à partir d'un état figé en début de drag. */
const shiftSelection = (ph: Phase, sel: MultiSel, dx: number, dy: number): Partial<Phase> => {
  const mv = (p: Pt) => ({ x: p.x + dx, y: p.y + dy });
  const ps = new Set(sel.players);
  const ds = new Set(sel.defenders);
  const as = new Set(sel.arrows);
  return {
    players: ph.players.map((p, i) => (ps.has(i) ? { ...p, ...mv(p) } : p)),
    defenders: ph.defenders.map((d, i) => (ds.has(i) ? { ...d, ...mv(d) } : d)),
    // les extrémités liées à un joueur suivent déjà le joueur
    arrows: ph.arrows.map((a, i) =>
      as.has(i)
        ? {
            ...a,
            ...(a.from && { from: mv(a.from) }),
            ...(a.to && { to: mv(a.to) }),
            ...(a.control && { control: mv(a.control) }),
          }
        : a
    ),
  };
};

/**
 * Supprime les éléments sélectionnés. Les flèches restantes liées à un joueur supprimé
 * gardent sa position comme extrémité libre ; les autres liens sont ré-indexés.
 */
const removeSelection = (ph: Phase, sel: MultiSel): Partial<Phase> => {
  const ps = new Set(sel.players);
  const ds = new Set(sel.defenders);
  const as = new Set(sel.arrows);
  const newIndex = new Map<number, number>();
  ph.players.forEach((_, i) => {
    if (!ps.has(i)) newIndex.set(i, newIndex.size);
  });
  const relink = (a: Phase["arrows"][number], end: "from" | "to") => {
    const key = end === "from" ? "fromPlayerIndex" : "toPlayerIndex";
    const idx = a[key];
    if (idx === undefined) return {};
    if (newIndex.has(idx)) return { [key]: newIndex.get(idx) };
    const p = ph.players[idx];
    return { [key]: undefined, [end]: p ? { x: p.x, y: p.y } : a[end] };
  };
  return {
    players: ph.players.filter((_, i) => !ps.has(i)),
    defenders: ph.defenders.filter((_, i) => !ds.has(i)),
    arrows: ph.arrows
      .filter((_, i) => !as.has(i))
      .map((a) => ({ ...a, ...relink(a, "from"), ...relink(a, "to") })),
  };
};

export const PlayCreator = ({
  mode = "create",
  initialPhases = [{ players: [], arrows: [], defenders: [] }],
  initialName = "",
  initialTags = [],
  playId = null,
  initialMeta = null as any | null,
}: {
  mode?: "create" | "edit";
  initialPhases?: any[];
  initialName?: string;
  initialTags?: string[];
  playId?: string | null;
  initialMeta?: any | null;
}) => {
  const { t } = useI18nSafe();
  const [image, status] = useImage("/court.png");
  const [stageWidth, setStageWidth] = useState(0);
  const [stageHeight, setStageHeight] = useState(0);
  const navigate = useNavigate();

  // --- Hauteur dispo : la page tient dans l'écran sans scroll ---
  const rootRef = useRef<HTMLDivElement>(null);
  const [availH, setAvailH] = useState<number | null>(null);
  useEffect(() => {
    const compute = () => {
      const el = rootRef.current;
      if (!el) return;
      // espace entre le haut de l'éditeur et le bas de la fenêtre
      const top = el.getBoundingClientRect().top;
      setAvailH(Math.max(480, Math.round(window.innerHeight - top)));
    };
    compute();
    // recalcs après stabilisation du layout (navbar async, polices, etc.)
    const timers = [60, 150, 400, 900].map((ms) => window.setTimeout(compute, ms));
    window.addEventListener("resize", compute);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(compute);
      ro.observe(document.body);
    }
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", compute);
      ro?.disconnect();
    };
  }, []);

  // --- Dark mode detection
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document !== "undefined") {
      const hasClass = document.documentElement.classList.contains("dark");
      if (hasClass) return true;
    }
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  const phaseFromStoredToCanvas = useCallback(
    (
      phase: any,
      w: number,
      h: number,
      meta?: {
        baseW?: number;
        baseH?: number;
        stageW?: number;
        stageH?: number;
        w?: number;
        h?: number;
        coordMode?: "normalized" | "pixel";
      }
    ) => {
      // Mesure rapide
      const measure = (ph: any) => {
        let maxX = 0,
          maxY = 0;
        (ph?.players ?? []).forEach((p: any) => {
          if (!p) return;
          maxX = Math.max(maxX, Number(p.x) || 0);
          maxY = Math.max(maxY, Number(p.y) || 0);
        });
        (ph?.defenders ?? []).forEach((d: any) => {
          if (!d) return;
          maxX = Math.max(maxX, Number(d.x) || 0);
          maxY = Math.max(maxY, Number(d.y) || 0);
        });
        (ph?.arrows ?? []).forEach((a: any) => {
          ["from", "to", "control"].forEach((k) => {
            const v = a?.[k];
            if (v) {
              maxX = Math.max(maxX, Number(v.x) || 0);
              maxY = Math.max(maxY, Number(v.y) || 0);
            }
          });
        });
        return { maxX, maxY };
      };

      const { maxX, maxY } = measure(phase);
      const inferredNormalized = maxX <= 1.01 && maxY <= 1.01;

      const coordMode = meta?.coordMode ?? (inferredNormalized ? "normalized" : "pixel");

      const baseW = coordMode === "normalized" ? 1 : meta?.baseW ?? meta?.stageW ?? meta?.w ?? (maxX || 1);
      const baseH = coordMode === "normalized" ? 1 : meta?.baseH ?? meta?.stageH ?? meta?.h ?? (maxY || 1);

      const scale = (pt?: { x: number; y: number }) => {
        if (!pt) return undefined;
        return coordMode === "normalized"
          ? { x: pt.x * w, y: pt.y * h }
          : { x: (pt.x / baseW) * w, y: (pt.y / baseH) * h };
      };

      return {
        ...phase,
        players: (phase.players ?? []).map((p: any) => ({ ...p, ...scale(p)! })),
        defenders: (phase.defenders ?? []).map((d: any) => ({ ...d, ...scale(d)! })),
        arrows: (phase.arrows ?? []).map((a: any) => ({
          ...a,
          from: a.fromPlayerIndex !== undefined ? undefined : scale(a.from),
          to: a.toPlayerIndex !== undefined ? undefined : scale(a.to),
          control: scale(a.control),
        })),
      };
    },
    []
  );

  useEffect(() => {
    const html = document.documentElement;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handleMq = (e: MediaQueryListEvent) => {
      if (!html.classList.contains("dark")) setIsDark(e.matches);
    };
    const obs = new MutationObserver(() => {
      setIsDark(html.classList.contains("dark"));
    });
    obs.observe(html, { attributes: true, attributeFilter: ["class"] });
    if (mql.addEventListener) mql.addEventListener("change", handleMq);
    else mql.addListener(handleMq);
    return () => {
      obs.disconnect();
      if (mql.removeEventListener) mql.removeEventListener("change", handleMq);
      else mql.removeListener(handleMq);
    };
  }, []);

  // ==== PHASES (avec défenseurs) ====
  const normalizeInit = (arr: any[]): Phase[] =>
    (arr || []).map((p: any) => ({
      players: p?.players ?? [],
      arrows: p?.arrows ?? [],
      defenders: p?.defenders ?? [],
    }));

  const [phases, setPhases] = useState<Phase[]>(() => normalizeInit(initialPhases));
  const [currentPhase, setCurrentPhase] = useState(0);

  // UI state
  const [name, setName] = useState(initialName);
  const [draggingItem, setDraggingItem] = useState<
    "player" | "arrow" | "dribble" | "cut" | "screen" | "shot" | "handoff" | "defender" | null
  >(null);
  const [selectedArrowIndex, setSelectedArrowIndex] = useState<number | null>(null);
  const [selectedPlayerIndex, setSelectedPlayerIndex] = useState<number | null>(null);
  const [selectedDefenderIndex, setSelectedDefenderIndex] = useState<number | null>(null);
  const [selectedPlayerNumber, setSelectedPlayerNumber] = useState<number | null>(null);
  // sélection multiple : rectangle tracé en glissant sur une zone vide du terrain
  const [multiSel, setMultiSel] = useState<MultiSel | null>(null);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const groupDragRef = useRef<{ start: Pt; snapshot: Phase } | null>(null);
  const [givingBall, setGivingBall] = useState(false);
  // garde l'outil actif après un placement (placer plusieurs éléments d'affilée)
  const [keepTool, setKeepTool] = useState(true);
  // position du curseur sur le terrain -> aperçu "fantôme"
  const [pointerPos, setPointerPos] = useState<{ x: number; y: number } | null>(null);

  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTags);
  const [profileExists, setProfileExists] = useState<boolean | null>(null);
  const stageAreaRef = useRef<HTMLDivElement>(null);

  // --- feedback UI (toast + validation) ---
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((msg: string, type: "success" | "error" | "info" = "info") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ msg, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // couleurs
  const ink = "#000000";
  const accent = isDark ? "#60a5fa" : "#2563eb";
  const warn = isDark ? "#f87171" : "#ef4444";
  const helper = isDark ? "#a78bfa" : "#7c3aed";
  const arrowStroke = "#000000";

  // Pions joueurs
  const PLAYER_RADIUS = 15;

  // Défenseur (moustache)
  const DEF_DOT_R = 12;
  const DEF_WING_LEN = 15;
  const DEF_WING_RISE = 2;
  const DEF_WING_STROKE = 5;
  const DEF_WING_GAP = 1;
  const DEF_ROT_STEP = 30;

  // ===== Helpers Normalisation / Détection =====
  const normPt = (pt: Pt, w: number, h: number): Pt => ({ x: +(pt.x / w).toFixed(6), y: +(pt.y / h).toFixed(6) });

  const phaseToNormalized = (phase: any, w: number, h: number) => ({
    ...phase,
    players: (phase.players ?? []).map((p: any) => ({ ...p, ...normPt(p, w, h) })),
    defenders: (phase.defenders ?? []).map((d: any) => ({ ...d, ...normPt(d, w, h) })),
    arrows: (phase.arrows ?? []).map((a: any) => ({
      ...a,
      from: a.fromPlayerIndex !== undefined ? undefined : a.from ? normPt(a.from, w, h) : undefined,
      to: a.toPlayerIndex !== undefined ? undefined : a.to ? normPt(a.to, w, h) : undefined,
      control: a.control ? normPt(a.control, w, h) : undefined,
    })),
  });

  // react-select styles
  const selectStyles = useMemo(
    () => ({
      control: (base: any) => ({
        ...base,
        backgroundColor: isDark ? "#1f2937" : "#ffffff",
        borderColor: isDark ? "#374151" : "#d1d5db",
        color: isDark ? "#e5e7eb" : "#111827",
      }),
      menu: (base: any) => ({
        ...base,
        backgroundColor: isDark ? "#111827" : "#ffffff",
        color: isDark ? "#f9fafb" : "#111827",
      }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isFocused ? (isDark ? "#374151" : "#e5e7eb") : isDark ? "#111827" : "#ffffff",
        color: isDark ? "#f9fafb" : "#111827",
      }),
      multiValue: (base: any) => ({ ...base, backgroundColor: isDark ? "#374151" : "#e5e7eb" }),
      multiValueLabel: (base: any) => ({ ...base, color: isDark ? "#f9fafb" : "#111827" }),
      input: (base: any) => ({ ...base, color: isDark ? "#f9fafb" : "#111827" }),
      singleValue: (base: any) => ({ ...base, color: isDark ? "#f9fafb" : "#111827" }),
    }),
    [isDark]
  );

  // phase courante
  const current = useMemo(
    () => phases[currentPhase] ?? { players: [], arrows: [], defenders: [] },
    [phases, currentPhase]
  );
  const players = current.players;
  const arrows = current.arrows;
  const defenders = useMemo<Defender[]>(() => current.defenders ?? [], [current.defenders]);

  // plus petit numéro (1, 2, 3…) pas encore utilisé sur le terrain — pas de limite à 5
  const nextPlayerNumber = useCallback(() => {
    const used = new Set(players.map((p) => p.number));
    let n = 1;
    while (used.has(n)) n++;
    return n;
  }, [players]);

  // data
  useEffect(() => {
    const fetchTags = async () => {
      const { data, error } = await supabase.from("play_tags").select("*");
      if (!error && data) setAvailableTags(data);
    };
    fetchTags();
  }, []);

  useEffect(() => {
    const checkProfile = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login");
        return;
      }
      const { data: profile, error } = await supabase
        .from("user_profiles")
        .select("id")
        .eq("id", user.id)
        .maybeSingle();
      if (error) {
        console.error("Erreur de chargement du profil :", error);
        setProfileExists(false);
      } else {
        setProfileExists(!!profile);
      }
    };
    checkProfile();
  }, [navigate]);

  // resize – calcule stageWidth/Height
  const prevWH = useRef<{ w: number; h: number } | null>(null);
  useEffect(() => {
    if (!image || status !== "loaded") return;
    const naturalW = image.naturalWidth;
    const naturalH = image.naturalHeight;
    const compute = () => {
      const el = stageAreaRef.current;
      const cw = el?.clientWidth ?? window.innerWidth;
      const ch = el?.clientHeight ?? window.innerHeight;
      const scale = Math.min(cw / naturalW, ch / naturalH);
      const newW = Math.floor(naturalW * scale);
      const newH = Math.floor(naturalH * scale);

      // re-scale positions si taille change
      if (prevWH.current) {
        const sx = newW / prevWH.current.w;
        const sy = newH / prevWH.current.h;
        if (Number.isFinite(sx) && Number.isFinite(sy) && (sx !== 1 || sy !== 1)) {
          setPhases((prev) =>
            prev.map((ph) => ({
              players: ph.players.map((p) => ({ ...p, x: p.x * sx, y: p.y * sy })),
              defenders: ph.defenders?.map((d) => ({ ...d, x: d.x * sx, y: d.y * sy })) ?? [],
              arrows: ph.arrows.map((a) => ({
                ...a,
                from:
                  a.fromPlayerIndex !== undefined
                    ? a.from
                    : a.from
                    ? { x: a.from.x * sx, y: a.from.y * sy }
                    : undefined,
                to:
                  a.toPlayerIndex !== undefined
                    ? a.to
                    : a.to
                    ? { x: a.to.x * sx, y: a.to.y * sy }
                    : undefined,
                control: a.control ? { x: a.control.x * sx, y: a.control.y * sy } : undefined,
              })),
            }))
          );
        }
      }

      setStageWidth(newW);
      setStageHeight(newH);
      prevWH.current = { w: newW, h: newH };
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (stageAreaRef.current) ro.observe(stageAreaRef.current);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [image, status]);

  // reprojection initiale
  const didInitFromStored = useRef(false);
  useEffect(() => {
    if (!stageWidth || !stageHeight || didInitFromStored.current) return;
    const meta = initialMeta || {};
    const converted = normalizeInit(initialPhases).map((ph) =>
      phaseFromStoredToCanvas(ph, stageWidth, stageHeight, meta)
    );
    setPhases(converted);
    didInitFromStored.current = true;
  }, [stageWidth, stageHeight, initialPhases, initialMeta, phaseFromStoredToCanvas]);

  // ==== updatePhase (merge partiel) ====
  const updatePhase = useCallback(
    (partial: Partial<Phase>) => {
      setPhases((prev) => {
        const copy = [...prev];
        const cur = copy[currentPhase] ?? { players: [], arrows: [], defenders: [] };
        copy[currentPhase] = { ...cur, ...partial };
        return copy;
      });
    },
    [currentPhase]
  );

  // ==== UNDO (Ctrl+Z) ====
  const MAX_HISTORY = 50;
  const [history, setHistory] = useState<Phase[][]>([]);
  const phasesRef = useRef(phases);
  useEffect(() => {
    phasesRef.current = phases;
  }, [phases]);

  const pushHistory = useCallback(() => {
    setHistory((h) => {
      const next = [...h, phasesRef.current];
      return next.length > MAX_HISTORY ? next.slice(next.length - MAX_HISTORY) : next;
    });
  }, []);

  const undo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) {
        showToast(t("playCreator:alerts.nothingToUndo"), "info");
        return h;
      }
      const prev = h[h.length - 1];
      setPhases(prev);
      setCurrentPhase((cp) => Math.min(cp, prev.length - 1));
      setSelectedArrowIndex(null);
      setSelectedPlayerIndex(null);
      setSelectedDefenderIndex(null);
      setMultiSel(null);
      return h.slice(0, -1);
    });
  }, [showToast, t]);

  // keydown (Delete)
  const playersRef = useRef(players);
  const arrowsRef = useRef(arrows);
  const defendersRef = useRef(defenders);
  const selArrowRef = useRef(selectedArrowIndex);
  const selPlayerRef = useRef(selectedPlayerIndex);
  const selDefRef = useRef(selectedDefenderIndex);
  useEffect(() => {
    playersRef.current = players;
  }, [players]);
  useEffect(() => {
    arrowsRef.current = arrows;
  }, [arrows]);
  useEffect(() => {
    defendersRef.current = defenders;
  }, [defenders]);
  useEffect(() => {
    selArrowRef.current = selectedArrowIndex;
  }, [selectedArrowIndex]);
  useEffect(() => {
    selPlayerRef.current = selectedPlayerIndex;
  }, [selectedPlayerIndex]);
  useEffect(() => {
    selDefRef.current = selectedDefenderIndex;
  }, [selectedDefenderIndex]);
  const multiSelRef = useRef(multiSel);
  useEffect(() => {
    multiSelRef.current = multiSel;
  }, [multiSel]);

  // la sélection multiple ne survit pas à une sélection simple ni à un changement de phase
  useEffect(() => {
    if (selectedArrowIndex !== null || selectedPlayerIndex !== null || selectedDefenderIndex !== null)
      setMultiSel(null);
  }, [selectedArrowIndex, selectedPlayerIndex, selectedDefenderIndex]);
  useEffect(() => {
    setMultiSel(null);
  }, [currentPhase, phases.length]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isEditable =
        !!target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
        if (isEditable) return; // laisse le undo natif du champ texte s'appliquer
        e.preventDefault();
        undo();
        return;
      }

      if (e.key === "Escape") {
        setDraggingItem(null);
        setGivingBall(false);
        setSelectedArrowIndex(null);
        setSelectedPlayerIndex(null);
        setSelectedDefenderIndex(null);
        setMultiSel(null);
        setMarquee(null);
        return;
      }

      // ---- Raccourcis d'outils (hors saisie texte) ----
      if (!isEditable && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (/^[1-5]$/.test(e.key)) {
          e.preventDefault();
          setDraggingItem("player");
          setGivingBall(false);
          setSelectedPlayerNumber(Number(e.key));
          return;
        }
        const toolMap: Record<
          string,
          "player" | "defender" | "arrow" | "dribble" | "cut" | "screen" | "shot" | "handoff"
        > = {
          p: "player",
          x: "defender",
          a: "arrow",
          d: "dribble",
          c: "cut",
          e: "screen",
          s: "shot",
          h: "handoff",
        };
        const k = e.key.toLowerCase();
        if (k === "b") {
          e.preventDefault();
          setGivingBall((v) => !v);
          setDraggingItem(null);
          return;
        }
        if (toolMap[k]) {
          e.preventDefault();
          const tgt = toolMap[k];
          setDraggingItem((prev) => (prev === tgt ? null : tgt));
          setGivingBall(false);
          return;
        }
      }

      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (isEditable) return; // ne pas supprimer l'élément sélectionné pendant une saisie texte
      const ph: Phase = {
        players: playersRef.current,
        arrows: arrowsRef.current,
        defenders: defendersRef.current,
      };
      const none: MultiSel = { players: [], defenders: [], arrows: [] };
      let sel: MultiSel | null = null;
      if (multiSelRef.current) sel = multiSelRef.current;
      else if (selArrowRef.current !== null) sel = { ...none, arrows: [selArrowRef.current] };
      else if (selPlayerRef.current !== null) sel = { ...none, players: [selPlayerRef.current] };
      else if (selDefRef.current !== null) sel = { ...none, defenders: [selDefRef.current] };
      if (!sel) return;

      e.preventDefault();
      pushHistory();
      updatePhase(removeSelection(ph, sel));
      setSelectedArrowIndex(null);
      setSelectedPlayerIndex(null);
      setSelectedDefenderIndex(null);
      setMultiSel(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [updatePhase, undo, pushHistory]);

  // handlers
  const handleStageClick = (e: any) => {
    const stage = e.target.getStage();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    // 1) add player
    if (draggingItem === "player") {
      const num = selectedPlayerNumber ?? nextPlayerNumber();
      const newPlayer = {
        x: pointer.x,
        y: pointer.y,
        hasBall: givingBall,
        number: num,
      };
      pushHistory();
      updatePhase({ players: [...players, newPlayer] });
      setGivingBall(false);
      if (keepTool) {
        // avance vers le prochain numéro libre pour enchaîner les placements
        const used = new Set([...players.map((p) => p.number), num]);
        let nextN = 1;
        while (used.has(nextN)) nextN++;
        setSelectedPlayerNumber(nextN);
      } else {
        setDraggingItem(null);
      }
      return;
    }

    // 1-bis) add DEFENDER
    if (draggingItem === "defender") {
      const newDef: Defender = { x: pointer.x, y: pointer.y, angle: 0 };
      pushHistory();
      updatePhase({ defenders: [...defenders, newDef] });
      setSelectedDefenderIndex(defenders.length);
      if (!keepTool) setDraggingItem(null);
      return;
    }

    // 2) add arrow
    if (["arrow", "dribble", "cut", "screen", "shot", "handoff"].includes(String(draggingItem))) {
      const newArrow = {
        from: { x: pointer.x - 30, y: pointer.y },
        to: { x: pointer.x + 30, y: pointer.y },
        type: draggingItem as string,
        control: { x: pointer.x, y: pointer.y - 40 },
      };
      pushHistory();
      updatePhase({ arrows: [...arrows, newArrow] });
      setSelectedArrowIndex(arrows.length);
      // un tracé (passe, dribble, cut, écran, tir, handoff) se pose à l'unité :
      // on désarme toujours l'outil pour rendre la souris libre après le placement.
      setDraggingItem(null);
      setPointerPos(null);
      return;
    }

    // 3) select arrow by ends
    const clickedArrowIndex = arrows.findIndex((arrow) => {
      const from = arrow.fromPlayerIndex !== undefined ? players[arrow.fromPlayerIndex] : arrow.from;
      const to = arrow.toPlayerIndex !== undefined ? players[arrow.toPlayerIndex] : arrow.to;
      if (!from || !to) return false;
      const distStart = Math.hypot(pointer.x - from.x, pointer.y - from.y);
      const distEnd = Math.hypot(pointer.x - to.x, pointer.y - to.y);
      return distStart < 10 || distEnd < 10;
    });
    if (clickedArrowIndex >= 0) {
      setSelectedArrowIndex(clickedArrowIndex);
      setSelectedPlayerIndex(null);
      setSelectedDefenderIndex(null);
      return;
    }

    // 3-bis) select defender
    const clickedDefIndex = defenders.findIndex(
      (d) => Math.hypot(pointer.x - d.x, pointer.y - d.y) < DEF_DOT_R + 10
    );
    if (clickedDefIndex >= 0) {
      setSelectedDefenderIndex(clickedDefIndex);
      setSelectedArrowIndex(null);
      setSelectedPlayerIndex(null);
      return;
    }

    // 4) select player
    const clickedPlayerIndex = players.findIndex((player) => {
      const dist = Math.hypot(pointer.x - player.x, pointer.y - player.y);
      return dist < 15;
    });
    if (clickedPlayerIndex >= 0) {
      setSelectedPlayerIndex(clickedPlayerIndex);
      setSelectedArrowIndex(null);
      setSelectedDefenderIndex(null);
      return;
    }

    // 5) clear + début d'un rectangle de sélection si on a appuyé sur une zone vide
    setSelectedArrowIndex(null);
    setSelectedPlayerIndex(null);
    setSelectedDefenderIndex(null);
    setMultiSel(null);
    if (e.target === stage) setMarquee({ x0: pointer.x, y0: pointer.y, x1: pointer.x, y1: pointer.y });
  };

  const finishMarquee = useCallback(() => {
    if (!marquee) return;
    setMarquee(null);
    const x = Math.min(marquee.x0, marquee.x1);
    const y = Math.min(marquee.y0, marquee.y1);
    const w = Math.abs(marquee.x1 - marquee.x0);
    const h = Math.abs(marquee.y1 - marquee.y0);
    if (w < 5 && h < 5) return; // simple clic dans le vide
    const inside = (p?: Pt) => !!p && p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h;
    const indicesWhere = <T,>(list: T[], pred: (item: T) => boolean) =>
      list.flatMap((item, i) => (pred(item) ? [i] : []));

    const sel: MultiSel = {
      players: indicesWhere(players, inside),
      defenders: indicesWhere(defenders, inside),
      arrows: indicesWhere(arrows, (a) => {
        const { from, to } = arrowEnds(a, players);
        return inside(from) && inside(to);
      }),
    };
    const n = selCount(sel);
    if (n === 0) return;
    if (n === 1) {
      // un seul élément : sélection classique (garde le panneau d'édition)
      if (sel.players.length) setSelectedPlayerIndex(sel.players[0]);
      else if (sel.defenders.length) setSelectedDefenderIndex(sel.defenders[0]);
      else setSelectedArrowIndex(sel.arrows[0]);
      return;
    }
    setMultiSel(sel);
  }, [marquee, players, defenders, arrows]);

  // relâcher la souris n'importe où (même hors du terrain) termine le rectangle
  useEffect(() => {
    if (!marquee) return;
    window.addEventListener("mouseup", finishMarquee);
    return () => window.removeEventListener("mouseup", finishMarquee);
  }, [marquee, finishMarquee]);

  // --- déplacement groupé des éléments sélectionnés ---
  const startGroupDrag = (start: Pt) => {
    pushHistory();
    groupDragRef.current = { start, snapshot: current };
  };
  const moveGroup = (pos: Pt) => {
    const g = groupDragRef.current;
    if (!g || !multiSel) return;
    updatePhase(shiftSelection(g.snapshot, multiSel, pos.x - g.start.x, pos.y - g.start.y));
  };
  const endGroupDrag = () => {
    groupDragRef.current = null;
  };
  // un appui sur un élément déjà sélectionné ne doit pas casser la sélection (on va le glisser)
  const keepMultiOnPress = (inMulti: boolean) => (e: KonvaEventObject<MouseEvent>) => {
    if (inMulti) e.cancelBubble = true;
  };

  const handleArrowDrag = (index: number, point: "from" | "to", pos: { x: number; y: number }) => {
    const updated = [...arrows];
    const snapDistance = 20;
    const playerIndex = players.findIndex((p) => Math.hypot(p.x - pos.x, p.y - pos.y) < snapDistance);
    const linkableTypes = ["arrow", "pass", "cut", "screen", "dribble", "handoff"];

    if (linkableTypes.includes(updated[index].type)) {
      if (playerIndex !== -1) {
        if (point === "from") updated[index].fromPlayerIndex = playerIndex;
        else {
          updated[index].toPlayerIndex = playerIndex;
          delete updated[index].to;
        }
      } else {
        if (point === "from") updated[index].from = pos;
        else {
          updated[index].to = pos;
          delete updated[index].toPlayerIndex;
        }
      }
    } else {
      if (point === "from") {
        updated[index].from = pos;
        delete updated[index].fromPlayerIndex;
      } else {
        updated[index].to = pos;
        delete updated[index].toPlayerIndex;
      }
    }
    updatePhase({ arrows: updated });
  };

  const handleDragMove = (index: number, pos: { x: number; y: number }) => {
    const updated = [...players];
    updated[index] = { ...updated[index], ...pos };
    updatePhase({ players: updated });
  };

  // suivi du curseur pour l'aperçu "fantôme"
  const handleStageMouseMove = (e: any) => {
    if (marquee) {
      const pt = e.target.getStage()?.getPointerPosition();
      if (pt) setMarquee((m) => (m ? { ...m, x1: pt.x, y1: pt.y } : m));
      return;
    }
    if (!draggingItem && !givingBall) {
      if (pointerPos) setPointerPos(null);
      return;
    }
    const stage = e.target.getStage();
    const pt = stage?.getPointerPosition();
    if (pt) setPointerPos({ x: pt.x, y: pt.y });
  };

  // --- édition sur le canvas (panneau contextuel) ---
  const setSelectedPlayerNumberValue = (n: number) => {
    if (selectedPlayerIndex === null) return;
    const next = players.map((p, idx) => (idx === selectedPlayerIndex ? { ...p, number: n } : p));
    pushHistory();
    updatePhase({ players: next });
  };

  const toggleSelectedBall = () => {
    if (selectedPlayerIndex === null) return;
    // plusieurs joueurs peuvent avoir un ballon : on ne touche qu'au joueur sélectionné
    const next = players.map((p, idx) =>
      idx === selectedPlayerIndex ? { ...p, hasBall: !p.hasBall } : p
    );
    pushHistory();
    updatePhase({ players: next });
  };

  const rotateSelectedDefender = (dir: 1 | -1) => {
    if (selectedDefenderIndex === null) return;
    const next = defenders.slice();
    const d = next[selectedDefenderIndex];
    next[selectedDefenderIndex] = { ...d, angle: ((d.angle || 0) + dir * DEF_ROT_STEP) % 360 };
    pushHistory();
    updatePhase({ defenders: next });
  };

  const movePhase = (dir: 1 | -1) => {
    const target = currentPhase + dir;
    if (target < 0 || target >= phases.length) return;
    pushHistory();
    setPhases((prev) => {
      const copy = [...prev];
      [copy[currentPhase], copy[target]] = [copy[target], copy[currentPhase]];
      return copy;
    });
    setCurrentPhase(target);
  };

  const goToPhase = (i: number) => {
    setCurrentPhase(i);
    setSelectedArrowIndex(null);
    setSelectedPlayerIndex(null);
    setSelectedDefenderIndex(null);
  };

  const convertInitialToCanvas = () => {
    const meta = initialMeta || {};
    return normalizeInit(initialPhases).map((ph) => phaseFromStoredToCanvas(ph, stageWidth, stageHeight, meta));
  };

  const handleReset = () => {
    const hasContent = phases.some((ph) => ph.players.length || ph.arrows.length || (ph.defenders?.length ?? 0));
    if (hasContent && !window.confirm(t("playCreator:alerts.confirmReset"))) return;
    if (hasContent) pushHistory();
    if (mode === "edit") {
      setPhases(convertInitialToCanvas());
      setName(initialName);
      setSelectedTagIds(initialTags);
      setCurrentPhase(0);
      setSelectedArrowIndex(null);
      setSelectedPlayerIndex(null);
      setSelectedDefenderIndex(null);
    } else {
      updatePhase({ players: [], arrows: [], defenders: [] });
      setName("");
      setSelectedTagIds([]);
      setCurrentPhase(0);
      setSelectedArrowIndex(null);
      setSelectedPlayerIndex(null);
      setSelectedDefenderIndex(null);
    }
    setDraggingItem(null);
    setGivingBall(false);
    setNameError(false);
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (!name.trim()) {
      setNameError(true);
      nameInputRef.current?.focus();
      showToast(t("playCreator:alerts.noName"), "error");
      return;
    }
    setIsSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showToast(t("playCreator:alerts.notLogged"), "error");
        return;
      }

      // normalise avant d’enregistrer
      const normalized = phases.map((ph) => phaseToNormalized(ph, stageWidth, stageHeight));

      if (mode === "edit" && playId) {
        const { error } = await supabase
          .from("plays")
          .update({ name, data: normalized, tag_ids: selectedTagIds })
          .eq("id", playId);
        if (error) {
          console.error(error);
          showToast(t("playCreator:alerts.updateError"), "error");
        } else {
          showToast(t("playCreator:alerts.updateSuccess"), "success");
          setTimeout(() => navigate("/allplays"), 600);
        }
      } else {
        const { error } = await supabase.from("plays").insert({
          id: uuidv4(),
          user_id: user.id,
          name,
          data: normalized,
          tag_ids: selectedTagIds,
          created_at: new Date().toISOString(),
        });
        if (error) {
          console.error(error);
          showToast(t("playCreator:alerts.saveError"), "error");
        } else {
          showToast(t("playCreator:alerts.saveSuccess"), "success");
          setTimeout(() => navigate("/allplays"), 600);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPhase = () => {
    pushHistory();
    type Movement = { x: number; y: number; type: string };
    const cur = phases[currentPhase];
    const copiedPlayers = cur.players.map((p: any) => ({ ...p, hasBall: false }));
    const movements: { [playerIndex: number]: Movement } = {};

    const priorityMap: Record<string, number> = { cut: 4, dribble: 3, handoff: 2, screen: 1 };
    const movementArrows = cur.arrows.filter((a: any) =>
      ["cut", "screen", "dribble", "handoff"].includes(a.type)
    );

    // Résout l'index du joueur d'origine de chaque flèche sans muter le state existant
    const resolvedFromIndex = new Map<any, number>();
    for (const arrow of movementArrows) {
      if (typeof arrow.fromPlayerIndex === "number") {
        resolvedFromIndex.set(arrow, arrow.fromPlayerIndex);
      } else if (arrow.from) {
        const { x, y } = arrow.from;
        const closest = copiedPlayers
          .map((p, i) => ({ index: i, distance: Math.hypot(p.x - x, p.y - y) }))
          .sort((a, b) => a.distance - b.distance)[0];
        if (closest && closest.distance < 40) resolvedFromIndex.set(arrow, closest.index);
      }
    }

    const arrowsByPlayer: { [index: number]: any[] } = {};
    for (const arrow of movementArrows) {
      const fromIndex = resolvedFromIndex.get(arrow);
      if (typeof fromIndex !== "number") continue;
      if (!arrowsByPlayer[fromIndex]) arrowsByPlayer[fromIndex] = [];
      arrowsByPlayer[fromIndex].push(arrow);
    }

    for (const indexStr of Object.keys(arrowsByPlayer)) {
      const index = parseInt(indexStr);
      const arrs = arrowsByPlayer[index];
      arrs.sort((a, b) => (priorityMap[b.type] || 0) - (priorityMap[a.type] || 0));
      const bestArrow = arrs[0];

      let targetX = 0;
      let targetY = 0;

      if (bestArrow.to) {
        targetX = bestArrow.to.x;
        targetY = bestArrow.to.y;
      } else if (typeof bestArrow.toPlayerIndex === "number" && copiedPlayers[bestArrow.toPlayerIndex]) {
        const p = copiedPlayers[bestArrow.toPlayerIndex];
        targetX = p.x;
        targetY = p.y;
      }

      movements[index] = { x: targetX, y: targetY, type: bestArrow.type };
    }

    for (const indexStr of Object.keys(movements)) {
      const index = parseInt(indexStr);
      const { x, y } = movements[index];
      copiedPlayers[index].x = x;
      copiedPlayers[index].y = y;
    }

    // Ballons (plusieurs possibles) : chacun suit son porteur. Une passe / un handoff le
    // transfère au receveur, un tir le retire ; un dribble le laisse au dribbleur.
    const nearestPlayer = (pt: { x: number; y: number } | undefined, positions: Pt[], max: number) => {
      if (!pt) return null;
      const closest = positions
        .map((p, i) => ({ index: i, distance: Math.hypot(p.x - pt.x, p.y - pt.y) }))
        .sort((a, b) => a.distance - b.distance)[0];
      return closest && closest.distance < max ? closest.index : null;
    };
    const before = cur.players.map((p) => ({ x: p.x, y: p.y }));
    const after = copiedPlayers.map((p) => ({ x: p.x, y: p.y }));
    const hasBall = cur.players.map((p) => !!p.hasBall);
    for (const a of cur.arrows as any[]) {
      if (!["arrow", "pass", "handoff", "shot"].includes(a.type)) continue;
      const fromIdx =
        typeof a.fromPlayerIndex === "number" ? a.fromPlayerIndex : nearestPlayer(a.from, before, 40);
      if (a.type === "shot") {
        if (fromIdx !== null && copiedPlayers[fromIdx]) hasBall[fromIdx] = false;
        continue;
      }
      const toIdx =
        typeof a.toPlayerIndex === "number" ? a.toPlayerIndex : nearestPlayer(a.to, after, 30);
      if (toIdx === null || !copiedPlayers[toIdx]) continue;
      if (fromIdx !== null && copiedPlayers[fromIdx] && fromIdx !== toIdx) hasBall[fromIdx] = false;
      hasBall[toIdx] = true;
    }
    copiedPlayers.forEach((p, i) => (p.hasBall = hasBall[i]));

    const newPhase: Phase = {
      players: copiedPlayers,
      arrows: [],
      defenders: (cur.defenders ?? []).map((d) => ({ ...d })),
    };
    setPhases((prev) => {
      const updated = [...prev];
      updated.splice(currentPhase + 1, 0, newPhase);
      return updated;
    });
    setCurrentPhase((p) => p + 1);
  };

  const handleDeletePhase = () => {
    if (phases.length === 1) {
      showToast(t("playCreator:alerts.cannotDeleteOnlyPhase"), "error");
      return;
    }
    const hasContent = current.players.length || current.arrows.length || (current.defenders?.length ?? 0);
    if (hasContent && !window.confirm(t("playCreator:alerts.confirmDeletePhase"))) return;
    pushHistory();
    setPhases((prev) => {
      const updated = [...prev];
      updated.splice(currentPhase, 1);
      return updated;
    });
    setCurrentPhase((prev) => Math.max(0, prev - 1));
    setSelectedArrowIndex(null);
    setSelectedPlayerIndex(null);
    setSelectedDefenderIndex(null);
  };

  if (profileExists === false) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">{t("playCreator:profile.requiredTitle")}</h2>
        </div>
      </div>
    );
  }

  if (profileExists === null) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <p className="text-gray-500 dark:text-gray-400">{t("playCreator:loading")}</p>
      </div>
    );
  }

  const activeToolLabel =
    draggingItem &&
    ({
      arrow: t("playCreator:actions.pass"),
      dribble: t("playCreator:actions.dribble"),
      cut: t("playCreator:actions.cut"),
      screen: t("playCreator:actions.screen"),
      shot: t("playCreator:actions.shot"),
      handoff: t("playCreator:actions.handoff"),
      player: `${t("playCreator:sections.addPlayers.title")} #${selectedPlayerNumber ?? ""}`,
      defender: t("playCreator:picker.defenderButton"),
    } as Record<string, string>)[draggingItem];

  // décalage du stage dans son conteneur (pour ancrer le panneau contextuel HTML)
  const stageOffset = () => {
    const el = stageAreaRef.current;
    if (!el) return { left: 0, top: 0 };
    return {
      left: Math.max(0, (el.clientWidth - stageWidth) / 2),
      top: Math.max(0, (el.clientHeight - stageHeight) / 2),
    };
  };
  const off = stageOffset();

  let ctxAnchor:
    | { x: number; y: number; kind: "player" | "defender" | "arrow" }
    | null = null;
  if (selectedPlayerIndex !== null && players[selectedPlayerIndex]) {
    const p = players[selectedPlayerIndex];
    ctxAnchor = { x: p.x, y: p.y, kind: "player" };
  } else if (selectedDefenderIndex !== null && defenders[selectedDefenderIndex]) {
    const d = defenders[selectedDefenderIndex];
    ctxAnchor = { x: d.x, y: d.y, kind: "defender" };
  } else if (selectedArrowIndex !== null && arrows[selectedArrowIndex]) {
    const a = arrows[selectedArrowIndex];
    const from = a.fromPlayerIndex !== undefined ? players[a.fromPlayerIndex] : a.from;
    const to = a.toPlayerIndex !== undefined ? players[a.toPlayerIndex] : a.to;
    if (from && to) ctxAnchor = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2, kind: "arrow" };
  }

  // panneau de la sélection multiple : centré au-dessus du groupe
  let multiAnchor: { x: number; y: number } | null = null;
  if (multiSel) {
    const pts: Pt[] = [
      ...multiSel.players.map((i) => players[i]),
      ...multiSel.defenders.map((i) => defenders[i]),
      ...multiSel.arrows.flatMap((i) => {
        const { from, to } = arrows[i] ? arrowEnds(arrows[i], players) : { from: undefined, to: undefined };
        return [from, to];
      }),
    ].filter((p): p is Pt => !!p);
    if (pts.length) {
      const xs = pts.map((p) => p.x);
      multiAnchor = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: Math.min(...pts.map((p) => p.y)) };
    }
  }

  const deleteMultiSel = () => {
    if (!multiSel) return;
    pushHistory();
    updatePhase(removeSelection(current, multiSel));
    setMultiSel(null);
  };

  const toolButtons = [
    ["arrow", "➡", "playCreator:actions.pass", "A"],
    ["dribble", "〜", "playCreator:actions.dribble", "D"],
    ["cut", "↝", "playCreator:actions.cut", "C"],
    ["screen", "⊤", "playCreator:actions.screen", "E"],
    ["shot", "🎯", "playCreator:actions.shot", "S"],
    ["handoff", "╳", "playCreator:actions.handoff", "H"],
  ] as const;

  return (
    <div
      ref={rootRef}
      style={{ height: availH ?? "100vh" }}
      className="flex w-full flex-col overflow-hidden bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100"
    >
      {/* toast */}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-white transition-opacity ${
            toast.type === "success" ? "bg-green-600" : toast.type === "error" ? "bg-red-600" : "bg-gray-800"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="flex flex-1 min-h-0 flex-col md:flex-row">
      {/* LEFT: court + toolbar + phases */}
      <div className="flex flex-col min-w-0 flex-1 overflow-hidden border-b md:border-b-0 md:border-r border-gray-300 dark:border-gray-700">
        {/* sticky toolbar */}
        <div className="sticky top-0 z-20 bg-white/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-center gap-1.5 px-2 py-2 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)]">
            {/* --- JOUEURS --- */}
            <div className="flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
              <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hidden lg:inline">
                {t("playCreator:sections.addPlayers.title")}
              </span>
              {[1, 2, 3, 4, 5].map((num) => {
                const active = draggingItem === "player" && selectedPlayerNumber === num;
                return (
                  <button
                    key={num}
                    type="button"
                    aria-pressed={active}
                    title={`${t("playCreator:sections.addPlayers.title")} ${num} · ${num}`}
                    className={`w-8 h-8 rounded-md text-sm font-bold transition-colors ${
                      active
                        ? "bg-blue-600 text-white ring-2 ring-blue-300"
                        : "bg-white text-blue-900 hover:bg-blue-50 dark:bg-gray-700 dark:text-blue-200 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => {
                      if (active) {
                        setDraggingItem(null);
                        return;
                      }
                      setDraggingItem("player");
                      setSelectedPlayerNumber(num);
                      setGivingBall(false);
                    }}
                  >
                    {num}
                  </button>
                );
              })}
              {(() => {
                // joueur supplémentaire (6, 7, …) : prend le prochain numéro libre
                const active =
                  draggingItem === "player" && selectedPlayerNumber !== null && selectedPlayerNumber > 5;
                return (
                  <button
                    type="button"
                    aria-pressed={active}
                    title={`${t("playCreator:sections.addPlayers.title")} +`}
                    className={`min-w-8 h-8 px-1.5 rounded-md text-sm font-bold transition-colors ${
                      active
                        ? "bg-blue-600 text-white ring-2 ring-blue-300"
                        : "bg-white text-blue-900 hover:bg-blue-50 dark:bg-gray-700 dark:text-blue-200 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => {
                      if (active) {
                        setDraggingItem(null);
                        return;
                      }
                      const used = new Set(players.map((p) => p.number));
                      let n = 6;
                      while (used.has(n)) n++;
                      setDraggingItem("player");
                      setSelectedPlayerNumber(n);
                      setGivingBall(false);
                    }}
                  >
                    {active ? selectedPlayerNumber : "+"}
                  </button>
                );
              })()}
              <button
                type="button"
                aria-pressed={draggingItem === "defender"}
                title={`${t("playCreator:picker.defenderButton")} · X`}
                className={`h-8 px-2 rounded-md text-sm font-bold transition-colors ${
                  draggingItem === "defender"
                    ? "bg-red-600 text-white ring-2 ring-red-300"
                    : "bg-white text-red-700 hover:bg-red-50 dark:bg-gray-700 dark:text-red-200 dark:hover:bg-gray-600"
                }`}
                onClick={() => setDraggingItem((p) => (p === "defender" ? null : "defender"))}
              >
                ✕
              </button>
            </div>

            {/* --- ACTIONS --- */}
            <div className="flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1">
              <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 hidden lg:inline">
                {t("playCreator:sections.addActions.title")}
              </span>
              {toolButtons.map(([tool, icon, key, sc]) => {
                const active = draggingItem === tool;
                return (
                  <button
                    key={tool}
                    type="button"
                    aria-pressed={active}
                    title={`${t(key)} · ${sc}`}
                    className={`h-8 px-2 rounded-md text-sm transition-colors flex items-center ${
                      active
                        ? "bg-blue-600 text-white ring-2 ring-blue-300"
                        : "bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600"
                    }`}
                    onClick={() => {
                      setDraggingItem((p) => (p === tool ? null : tool));
                      setGivingBall(false);
                    }}
                  >
                    <span aria-hidden>{icon}</span>
                    <span className="ml-1 hidden xl:inline">{t(key)}</span>
                  </button>
                );
              })}
            </div>

            {/* --- UTILITAIRES --- */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-pressed={keepTool}
                onClick={() => setKeepTool((v) => !v)}
                title={t("playCreator:v2.keepToolHint")}
                className={`h-8 px-2 rounded-md text-sm flex items-center gap-1 transition-colors ${
                  keepTool
                    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {keepTool ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                <span className="hidden sm:inline">{t("playCreator:v2.keepTool")}</span>
              </button>
              <button
                type="button"
                onClick={undo}
                disabled={history.length === 0}
                title={t("playCreator:toolbar.undo")}
                className="h-8 px-2 rounded-md text-sm flex items-center gap-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Undo2 className="w-4 h-4" />
                <span className="hidden sm:inline">{t("playCreator:v2.undo")}</span>
              </button>
            </div>
          </div>

          {/* ligne d'état */}
          <div className="flex items-center justify-center gap-2 px-2 pb-2 text-xs min-h-[1.75rem]">
            {activeToolLabel || givingBall ? (
              <>
                <span className="font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200">
                  {givingBall
                    ? t("playCreator:v2.ballActive")
                    : t("playCreator:v2.toolActive", { tool: activeToolLabel })}
                </span>
                <button
                  className="underline text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  onClick={() => {
                    setDraggingItem(null);
                    setGivingBall(false);
                  }}
                >
                  {t("playCreator:buttons2.cancelTool")} (Esc)
                </button>
              </>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">{t("playCreator:v2.selectHint")}</span>
            )}
          </div>
        </div>

        {/* stage + bande de phases */}
        <div className="flex-1 flex flex-col min-h-0">
        {/* stage area */}
        <div
          ref={stageAreaRef}
          className={`relative flex-1 flex items-center justify-center overflow-hidden ${
            draggingItem || givingBall ? "cursor-crosshair" : ""
          }`}
        >
          <Stage
            width={stageWidth}
            height={stageHeight}
            onMouseDown={handleStageClick}
            onMouseMove={handleStageMouseMove}
            onMouseLeave={() => setPointerPos(null)}
          >
            <Layer>
              {image && <KonvaImage image={image} width={stageWidth} height={stageHeight} listening={false} />}

              {/* === DÉFENSEURS === */}
              {defenders.map((d, i) => {
                const inMulti = !!multiSel?.defenders.includes(i);
                return (
                <Group
                  key={`def-${i}`}
                  x={d.x}
                  y={d.y}
                  rotation={d.angle || 0}
                  draggable
                  onMouseDown={keepMultiOnPress(inMulti)}
                  onClick={(e: KonvaEventObject<MouseEvent>) => {
                    e.cancelBubble = true;
                    const delta = e.evt.shiftKey ? -DEF_ROT_STEP : DEF_ROT_STEP;
                    const next = defenders.slice();
                    next[i] = { ...d, angle: ((d.angle || 0) + delta) % 360 };
                    setSelectedDefenderIndex(i);
                    setSelectedPlayerIndex(null);
                    setSelectedArrowIndex(null);
                    pushHistory();
                    updatePhase({ defenders: next });
                  }}
                  onDragStart={(e) =>
                    inMulti ? startGroupDrag({ x: e.target.x(), y: e.target.y() }) : pushHistory()
                  }
                  onDragMove={(e) => {
                    if (inMulti) return moveGroup({ x: e.target.x(), y: e.target.y() });
                    const next = defenders.slice();
                    next[i] = { ...d, x: e.target.x(), y: e.target.y() };
                    updatePhase({ defenders: next });
                  }}
                  onDragEnd={endGroupDrag}
                >
                  <Circle radius={DEF_DOT_R} fill={warn} />
                  <Shape
                    sceneFunc={(ctx, shape) => {
                      const x0 = -(DEF_DOT_R + DEF_WING_GAP),
                        y0 = 0;
                      const x1 = x0 - DEF_WING_LEN,
                        y1 = -DEF_WING_RISE;
                      const cx = x0 - DEF_WING_LEN * 0.55,
                        cy = -DEF_WING_RISE * 1.2;
                      ctx.beginPath();
                      ctx.lineCap = "round";
                      ctx.moveTo(x0, y0);
                      ctx.quadraticCurveTo(cx, cy, x1, y1);
                      ctx.strokeShape(shape);
                    }}
                    stroke={warn}
                    strokeWidth={DEF_WING_STROKE}
                    listening={false}
                  />
                  <Shape
                    sceneFunc={(ctx, shape) => {
                      const x0 = DEF_DOT_R + DEF_WING_GAP,
                        y0 = 0;
                      const x1 = x0 + DEF_WING_LEN,
                        y1 = -DEF_WING_RISE;
                      const cx = x0 + DEF_WING_LEN * 0.55,
                        cy = -DEF_WING_RISE * 1.2;
                      ctx.beginPath();
                      ctx.lineCap = "round";
                      ctx.moveTo(x0, y0);
                      ctx.quadraticCurveTo(cx, cy, x1, y1);
                      ctx.strokeShape(shape);
                    }}
                    stroke={warn}
                    strokeWidth={DEF_WING_STROKE}
                    listening={false}
                  />
                  {(selectedDefenderIndex === i || inMulti) && (
                    <Circle radius={DEF_DOT_R + 7} stroke={accent} strokeWidth={1.5} dash={[4, 2]} />
                  )}
                </Group>
                );
              })}

              {/* === ARROWS === */}
              {arrows.map((arrow, i) => {
                const isSelected = selectedArrowIndex === i;
                const from =
                  arrow.fromPlayerIndex !== undefined ? players[arrow.fromPlayerIndex] : arrow.from;
                const to = arrow.toPlayerIndex !== undefined ? players[arrow.toPlayerIndex] : arrow.to;
                const control = arrow.control;
                if (!from || !to) return null;
                const inMulti = !!multiSel?.arrows.includes(i);

                return (
                  <Group
                    key={`arrow-wrap-${i}`}
                    // en sélection multiple, la flèche entière sert de poignée pour déplacer le groupe.
                    // Le Group reste à (0,0) : Konva donne le déplacement total, qu'on applique au
                    // state avant de remettre le Group à l'origine.
                    draggable={inMulti}
                    onMouseDown={keepMultiOnPress(inMulti)}
                    // les poignées internes (extrémités, contrôle, handoff) ont leur propre drag
                    onDragStart={(e) => e.target === e.currentTarget && startGroupDrag({ x: 0, y: 0 })}
                    onDragMove={(e) => {
                      if (e.target !== e.currentTarget) return;
                      moveGroup({ x: e.target.x(), y: e.target.y() });
                      e.target.position({ x: 0, y: 0 });
                    }}
                    onDragEnd={endGroupDrag}
                    onClick={() => {
                      setSelectedArrowIndex(i);
                      setSelectedPlayerIndex(null);
                      setSelectedDefenderIndex(null);
                    }}
                  >
                    <PlayArrow
                      type={arrow.type}
                      from={from}
                      to={to}
                      control={control}
                      fromIsPlayer={arrow.fromPlayerIndex !== undefined}
                      toIsPlayer={arrow.toPlayerIndex !== undefined}
                      color={arrowStroke}
                      onHandoffDragStart={() => pushHistory()}
                      onHandoffDragMove={(dx, dy) => {
                        const updated = [...arrows];
                        const cur = updated[i];
                        updated[i] = {
                          ...cur,
                          ...(cur.from && { from: { x: cur.from.x + dx, y: cur.from.y + dy } }),
                          ...(cur.to && { to: { x: cur.to.x + dx, y: cur.to.y + dy } }),
                        };
                        updatePhase({ arrows: updated });
                      }}
                    />

                    {inMulti && (
                      <>
                        <Circle x={from.x} y={from.y} radius={5} fill={accent} listening={false} />
                        <Circle x={to.x} y={to.y} radius={5} fill={accent} listening={false} />
                      </>
                    )}

                    {/* Points d’édition */}
                    {isSelected && (
                      <>
                        {(arrow.type === "cut" || arrow.type === "dribble" || arrow.type === "screen") &&
                          control && (
                            <Circle
                              x={control.x}
                              y={control.y}
                              radius={6}
                              fill={helper}
                              draggable
                              onMouseDown={(e) => {
                                e.cancelBubble = true;
                              }}
                              onDragStart={() => pushHistory()}
                              onDragMove={(e) => {
                                const updated = [...arrows];
                                updated[i].control = { x: e.target.x(), y: e.target.y() };
                                updatePhase({ arrows: updated });
                              }}
                            />
                          )}
                        <Circle
                          x={from.x}
                          y={from.y}
                          radius={6}
                          fill={arrow.type === "handoff" ? "transparent" : accent}
                          draggable={arrow.type !== "handoff"}
                          onDragStart={() => pushHistory()}
                          onDragMove={(e) => handleArrowDrag(i, "from", { x: e.target.x(), y: e.target.y() })}
                        />
                        <Circle
                          x={to.x}
                          y={to.y}
                          radius={6}
                          fill={arrow.type === "handoff" ? "transparent" : accent}
                          draggable={arrow.type !== "handoff"}
                          onDragStart={() => pushHistory()}
                          onDragMove={(e) => handleArrowDrag(i, "to", { x: e.target.x(), y: e.target.y() })}
                        />
                      </>
                    )}
                  </Group>
                );
              })}

              {/* === PLAYERS === */}
              {players.map((p, i) => {
                const isHoveredByArrow =
                  selectedArrowIndex !== null &&
                  (() => {
                    const arrow = arrows[selectedArrowIndex!];
                    if (!arrow) return false;
                    const extremities: Pt[] = [];
                    if (arrow.fromPlayerIndex === undefined && arrow.from) extremities.push(arrow.from);
                    if (arrow.toPlayerIndex === undefined && arrow.to) extremities.push(arrow.to);
                    return extremities.some((pos) => Math.hypot(pos.x - p.x, pos.y - p.y) < 25);
                  })();

                const inMulti = !!multiSel?.players.includes(i);

                return (
                  <Group
                    key={i}
                    x={p.x}
                    y={p.y}
                    draggable
                    onMouseDown={keepMultiOnPress(inMulti)}
                    onClickCapture={(e: KonvaEventObject<MouseEvent>) => {
                      e.cancelBubble = true;
                      if (givingBall) {
                        const updated = players.map((pp, index) =>
                          index === i ? { ...pp, hasBall: !pp.hasBall } : pp
                        );
                        pushHistory();
                        updatePhase({ players: updated });
                        setGivingBall(false);
                      } else {
                        setSelectedPlayerIndex(i);
                        setSelectedArrowIndex(null);
                        setSelectedDefenderIndex(null);
                      }
                    }}
                    onDragStart={(e) =>
                      inMulti ? startGroupDrag({ x: e.target.x(), y: e.target.y() }) : pushHistory()
                    }
                    onDragMove={(e) =>
                      inMulti
                        ? moveGroup({ x: e.target.x(), y: e.target.y() })
                        : handleDragMove(i, { x: e.target.x(), y: e.target.y() })
                    }
                    onDragEnd={endGroupDrag}
                  >
                    {isHoveredByArrow && <Circle radius={22} stroke={helper} strokeWidth={2} dash={[4, 2]} />}

                    {p.hasBall ? (
                      <>
                        <Circle radius={PLAYER_RADIUS} stroke={ink} strokeWidth={2} />
                        <Text
                          text={p.number.toString()}
                          fontSize={18}
                          fontStyle="bold"
                          fill={ink}
                          align="center"
                          verticalAlign="middle"
                          width={PLAYER_RADIUS * 2}
                          height={PLAYER_RADIUS * 2}
                          offsetX={PLAYER_RADIUS}
                          offsetY={PLAYER_RADIUS}
                        />
                      </>
                    ) : (
                      <Text
                        text={p.number.toString()}
                        fontSize={18}
                        fontStyle="bold"
                        fill={ink}
                        align="center"
                        verticalAlign="middle"
                        width={PLAYER_RADIUS * 2}
                        height={PLAYER_RADIUS * 2}
                        offsetX={PLAYER_RADIUS}
                        offsetY={PLAYER_RADIUS}
                      />
                    )}

                    {(selectedPlayerIndex === i || inMulti) && (
                      <Circle radius={PLAYER_RADIUS + 5} stroke={warn} strokeWidth={2} dash={[4, 2]} />
                    )}
                  </Group>
                );
              })}
              {/* === RECTANGLE DE SÉLECTION === */}
              {marquee && (
                <Rect
                  x={Math.min(marquee.x0, marquee.x1)}
                  y={Math.min(marquee.y0, marquee.y1)}
                  width={Math.abs(marquee.x1 - marquee.x0)}
                  height={Math.abs(marquee.y1 - marquee.y0)}
                  fill={isDark ? "rgba(96,165,250,0.15)" : "rgba(37,99,235,0.1)"}
                  stroke={accent}
                  strokeWidth={1}
                  dash={[4, 3]}
                  listening={false}
                />
              )}

              {/* === APERÇU FANTÔME (suit le curseur) === */}
              {pointerPos && (draggingItem || givingBall) && (
                <Group x={pointerPos.x} y={pointerPos.y} listening={false} opacity={0.5}>
                  {givingBall && !draggingItem && (
                    <Text text="🏀" fontSize={24} offsetX={12} offsetY={12} />
                  )}
                  {draggingItem === "player" && (
                    <>
                      <Circle radius={PLAYER_RADIUS} stroke={ink} strokeWidth={2} dash={[4, 3]} />
                      <Text
                        text={String(selectedPlayerNumber ?? nextPlayerNumber())}
                        fontSize={18}
                        fontStyle="bold"
                        fill={ink}
                        align="center"
                        verticalAlign="middle"
                        width={PLAYER_RADIUS * 2}
                        height={PLAYER_RADIUS * 2}
                        offsetX={PLAYER_RADIUS}
                        offsetY={PLAYER_RADIUS}
                      />
                    </>
                  )}
                  {draggingItem === "defender" && (
                    <>
                      <Circle radius={DEF_DOT_R} fill={warn} />
                      <Line
                        points={[-(DEF_DOT_R + 2), 0, -(DEF_DOT_R + 2) - DEF_WING_LEN, -DEF_WING_RISE]}
                        stroke={warn}
                        strokeWidth={DEF_WING_STROKE}
                        lineCap="round"
                      />
                      <Line
                        points={[DEF_DOT_R + 2, 0, DEF_DOT_R + 2 + DEF_WING_LEN, -DEF_WING_RISE]}
                        stroke={warn}
                        strokeWidth={DEF_WING_STROKE}
                        lineCap="round"
                      />
                    </>
                  )}
                  {["arrow", "dribble", "cut", "screen", "shot", "handoff"].includes(
                    String(draggingItem)
                  ) && (
                    <KonvaArrow
                      points={[-32, 0, 32, 0]}
                      stroke={arrowStroke}
                      fill={arrowStroke}
                      strokeWidth={3}
                      pointerLength={9}
                      pointerWidth={9}
                      dash={draggingItem === "arrow" || draggingItem === "dribble" ? [8, 5] : undefined}
                    />
                  )}
                </Group>
              )}
            </Layer>
          </Stage>

          {/* panneau contextuel — édition directe sur le terrain */}
          {ctxAnchor && ctxAnchor.kind !== "arrow" && stageWidth > 0 && (
            <div
              className="absolute z-30 -translate-x-1/2 -translate-y-full pointer-events-none"
              style={{ left: off.left + ctxAnchor.x, top: off.top + ctxAnchor.y - 20 }}
            >
              <div className="pointer-events-auto flex items-center gap-1 rounded-lg bg-gray-900/95 text-white shadow-xl px-1.5 py-1 text-xs">
                {ctxAnchor.kind === "player" && selectedPlayerIndex !== null && (
                  <>
                    <div className="flex items-center gap-0.5 pr-1 mr-0.5 border-r border-white/20">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          onClick={() => setSelectedPlayerNumberValue(n)}
                          className={`w-5 h-5 rounded text-[11px] font-bold ${
                            players[selectedPlayerIndex]?.number === n
                              ? "bg-blue-500"
                              : "hover:bg-white/15"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                      <input
                        key={`${selectedPlayerIndex}-${players[selectedPlayerIndex]?.number}`}
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={99}
                        defaultValue={players[selectedPlayerIndex]?.number}
                        aria-label="#"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        }}
                        onBlur={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (Number.isFinite(v) && v >= 0 && v !== players[selectedPlayerIndex]?.number)
                            setSelectedPlayerNumberValue(Math.min(v, 99));
                        }}
                        className={`w-9 h-5 ml-0.5 rounded px-1 text-[11px] font-bold text-center bg-white/10 focus:bg-white/20 outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                          (players[selectedPlayerIndex]?.number ?? 0) > 5 ? "ring-1 ring-blue-400" : ""
                        }`}
                      />
                    </div>
                    <button
                      onClick={toggleSelectedBall}
                      className="h-6 px-1.5 rounded hover:bg-white/15 whitespace-nowrap"
                    >
                      {players[selectedPlayerIndex]?.hasBall
                        ? t("playCreator:v2.removeBall")
                        : t("playCreator:v2.giveBall")}
                    </button>
                  </>
                )}
                {ctxAnchor.kind === "defender" && (
                  <>
                    <button
                      onClick={() => rotateSelectedDefender(-1)}
                      title={t("playCreator:v2.rotateLeft")}
                      className="w-6 h-6 rounded hover:bg-white/15 flex items-center justify-center"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => rotateSelectedDefender(1)}
                      title={t("playCreator:v2.rotateRight")}
                      className="w-6 h-6 rounded hover:bg-white/15 flex items-center justify-center"
                    >
                      <RotateCw className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
          {multiSel && multiAnchor && stageWidth > 0 && (
            <div
              className="absolute z-30 -translate-x-1/2 -translate-y-full pointer-events-none"
              style={{ left: off.left + multiAnchor.x, top: off.top + multiAnchor.y - 22 }}
            >
              <div className="pointer-events-auto flex items-center gap-1 rounded-lg bg-gray-900/95 text-white shadow-xl px-1.5 py-1 text-xs">
                <span className="px-1 whitespace-nowrap">
                  {t("playCreator:v2.selectedCount", { count: selCount(multiSel) })}
                </span>
                <button
                  onClick={deleteMultiSel}
                  title={t("playCreator:v2.delete")}
                  className="w-6 h-6 rounded hover:bg-red-500/80 flex items-center justify-center"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* bande de navigation des phases */}
        <div className="flex items-center justify-center gap-1 px-2 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 overflow-x-auto">
          <span className="shrink-0 mr-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {t("playCreator:v2.phases")}
          </span>
          <button
            onClick={() => movePhase(-1)}
            disabled={currentPhase === 0}
            title={t("playCreator:v2.movePhaseLeft")}
            className="shrink-0 w-7 h-7 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 flex items-center justify-center"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {phases.map((ph, i) => {
            const isCur = i === currentPhase;
            const count = ph.players.length + ph.arrows.length + (ph.defenders?.length ?? 0);
            return (
              <button
                key={i}
                onClick={() => goToPhase(i)}
                title={`${t("playCreator:v2.phase")} ${i + 1}`}
                className={`shrink-0 h-7 px-2.5 rounded-md text-xs font-semibold transition-colors ${
                  isCur
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                }`}
              >
                {i + 1}
                <span className={`ml-1 font-normal ${isCur ? "text-blue-100" : "text-gray-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => movePhase(1)}
            disabled={currentPhase === phases.length - 1}
            title={t("playCreator:v2.movePhaseRight")}
            className="shrink-0 w-7 h-7 rounded-md bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40 flex items-center justify-center"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="shrink-0 w-px h-5 bg-gray-300 dark:bg-gray-600 mx-1" />
          <button
            onClick={handleAddPhase}
            title={t("playCreator:v2.addPhase")}
            className="shrink-0 h-7 px-2 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{t("playCreator:toolbar.addPhase")}</span>
          </button>
          <button
            onClick={handleDeletePhase}
            disabled={phases.length === 1}
            title={
              phases.length === 1
                ? t("playCreator:toolbar.deletePhaseTooltipOnly")
                : t("playCreator:v2.deletePhase")
            }
            className="shrink-0 w-7 h-7 rounded-md bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-200 disabled:opacity-40 flex items-center justify-center"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        </div>
      </div>

      {/* RIGHT: sidebar */}
      <div className="md:w-[320px] w-full flex flex-col bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 max-h-[42vh] md:max-h-none">
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5">
          <div>
            <label
              htmlFor="playName"
              className="block font-bold text-sm mb-1 text-gray-700 dark:text-gray-300"
            >
              {t("playCreator:v2.name")}
            </label>
            <input
              id="playName"
              type="text"
              className={`border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded px-3 py-2 w-full ${
                nameError
                  ? "border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                  : "border-gray-300 dark:border-gray-700"
              }`}
              value={name}
              ref={nameInputRef}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(false);
              }}
              placeholder={t("playCreator:input.placeholderName")}
              aria-invalid={nameError}
            />
            {nameError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{t("playCreator:alerts.noName")}</p>
            )}
          </div>

          <div>
            <h3 className="font-bold text-sm mb-2 text-gray-700 dark:text-gray-300">
              {t("playCreator:sections.tags.title")}
            </h3>
            <Select<Option, true>
              isMulti
              styles={selectStyles}
              options={availableTags.map((tag) => ({ value: tag.id, label: tag.name }))}
              value={availableTags
                .filter((tag) => selectedTagIds.includes(tag.id))
                .map((tag) => ({ value: tag.id, label: tag.name }))}
              onChange={(selected: MultiValue<Option>) => {
                setSelectedTagIds(selected.map((s) => s.value));
              }}
              className="text-sm"
              placeholder={t("playCreator:tags.placeholder")}
            />
          </div>

          <details className="text-xs text-gray-500 dark:text-gray-400">
            <summary className="cursor-pointer font-semibold text-gray-600 dark:text-gray-300 select-none">
              {t("playCreator:v2.shortcutsTitle")}
            </summary>
            <ul className="mt-2 space-y-1.5">
              <li>
                <Kbd>1</Kbd>–<Kbd>5</Kbd> · {t("playCreator:sections.addPlayers.title")}
              </li>
              <li>
                <Kbd>B</Kbd> {t("playCreator:picker.ballHandlerButton")} · <Kbd>X</Kbd>{" "}
                {t("playCreator:picker.defenderButton")}
              </li>
              <li>
                <Kbd>A</Kbd> {t("playCreator:actions.pass")} · <Kbd>D</Kbd> {t("playCreator:actions.dribble")} ·{" "}
                <Kbd>C</Kbd> {t("playCreator:actions.cut")}
              </li>
              <li>
                <Kbd>E</Kbd> {t("playCreator:actions.screen")} · <Kbd>S</Kbd> {t("playCreator:actions.shot")} ·{" "}
                <Kbd>H</Kbd> {t("playCreator:actions.handoff")}
              </li>
              <li>
                <Kbd>Esc</Kbd> · <Kbd>Suppr</Kbd> · <Kbd>Ctrl</Kbd>+<Kbd>Z</Kbd>
              </li>
            </ul>
          </details>
        </div>

        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex gap-2">
          <button
            className="flex-1 px-3 py-2 bg-white text-red-600 border border-red-300 hover:bg-red-50 dark:bg-gray-700 dark:text-red-300 dark:border-red-800 dark:hover:bg-gray-600 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleReset}
            disabled={isSaving}
          >
            {t("playCreator:buttons.reset")}
          </button>
          <button
            className="flex-[2] px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving && (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            )}
            {isSaving ? t("playCreator:buttons2.saving") : t("playCreator:buttons.save")}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};
